/**
 * PmsReservationDetail — `/pms/reservations/:id`, la ficha de la vista previa V2.
 *
 * Es la superficie operativa de una estancia. Se abre en una página propia (no
 * en el panel lateral compartido, que sigue siendo solo claro) para que
 * funcione igual de bien en escritorio y en móvil.
 *
 * Lo que esta pantalla defiende:
 *   - reserva ≠ huésped ≠ unidad física;
 *   - los tres ejes de estado son independientes y ninguno se mueve solo;
 *   - lo reservado es un tipo de alojamiento y la unidad se asigna después,
 *     siempre dentro del mismo tipo;
 *   - cancelar no borra.
 *
 * Todas las acciones son de demostración: cambian el estado de sesión y se
 * pierden al recargar. No hay backend, ni pasarela, ni mensajería.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import PmsShell from "@/pages/admin/pms/PmsShell";
import { ArrowLeft, DoorOpen, SignOut, Prohibit, WarningCircle } from "@phosphor-icons/react";

import { DEMO_TODAY_ISO, BOOKING_STATUS_LABEL, PAYMENT_STATUS_LABEL } from "@/data/pmsDemoData";
import { formatMoney } from "@/lib/pmsSelectors";
import { PAYMENT_EVENT_KINDS } from "@/data/reservationsV2Demo";
import { useSettingsV2, unitLabelFor } from "@/lib/settingsV2Store";
import {
  usePmsReservations, updateReservation, cancelReservation, demoNowISO,
} from "@/lib/pmsRuntimeStore";
import { useTodayV2, addServiceOrder, removeRequest } from "@/lib/todayV2Store";
import { useReservationsV2, addPaymentEvent, logActivity } from "@/lib/reservationsV2Store";
import { useGuestsV2, verifyDocument, fileImmigration } from "@/lib/guestsV2Store";
import { enrichRequests } from "@/lib/todayV2Selectors";
import { buildReservationView, isUnitEligible } from "@/lib/reservationsV2Selectors";
import { SURFACE, PreviewToast, QuickAction } from "@/pages/admin/pms/v2/V2Primitives";
import {
  OverviewSection, GuestsSection, StaySection, PaymentsSection, ServicesSection,
} from "@/pages/admin/pms/reservations/ReservationDetailSections";
import {
  MessagesSection, OperationsSection, ActivitySection,
} from "@/pages/admin/pms/reservations/ReservationDetailTimeline";
import CancelDialog from "@/pages/admin/pms/reservations/CancelDialog";

export default function PmsReservationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const settings = useSettingsV2();
  const reservations = usePmsReservations();
  const todayState = useTodayV2();
  const reservationsState = useReservationsV2();
  const guestState = useGuestsV2();

  const unitLabel = useMemo(() => unitLabelFor(settings.property), [settings.property]);
  const requests = useMemo(
    () => enrichRequests({ state: todayState, services: settings.services, unitLabel }),
    [todayState, settings.services, unitLabel],
  );

  const reservation = reservations.find((r) => r.reservation_id === id) || null;
  const view = useMemo(() => (reservation
    ? buildReservationView({
      reservation, settings, requests, storeState: reservationsState,
      guestState, today: DEMO_TODAY_ISO,
    })
    : null), [reservation, settings, requests, reservationsState, guestState]);

  const [toast, setToast] = useState(null);
  const [cancelOpen, setCancelOpen] = useState(false);
  const toastTimer = useRef(null);

  const showToast = useCallback((text = "Actualizado en la vista previa") => {
    setToast(text);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2400);
  }, []);

  useEffect(() => () => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
  }, []);

  const onAssign = useCallback((unitId, { mode, reason }) => {
    if (!reservation || !isUnitEligible(reservation, unitId, settings)) return;
    updateReservation(reservation.reservation_id, {
      assigned_villa_id: unitId,
      allocation: {
        ...(reservation.allocation || {}),
        recommended_villa_id: reservation.allocation?.recommended_villa_id || unitId,
        assigned_villa_id: unitId,
        mode: mode === "manual_override" ? "manual_override" : "automatic",
        manual_override: mode === "manual_override" ? {
          reason: reason || "Asignación manual",
          actor_user_id: "user_admin_001",
          actor_name: "Dolce Admin",
          at: demoNowISO(),
        } : null,
      },
    });
    logActivity(reservation.reservation_id, {
      kind: mode === "manual_override" ? "override" : "assigned",
      detail: `${unitId}${reason ? ` · ${reason}` : ""}`,
      at: demoNowISO(),
    });
    showToast();
  }, [reservation, settings, showToast]);

  const onUnassign = useCallback(() => {
    if (!reservation) return;
    updateReservation(reservation.reservation_id, {
      assigned_villa_id: null,
      allocation: null,
    });
    logActivity(reservation.reservation_id, { kind: "unassigned", at: demoNowISO() });
    showToast();
  }, [reservation, showToast]);

  const onStay = useCallback((next) => {
    if (!reservation) return;
    updateReservation(reservation.reservation_id, { stay_status: next });
    logActivity(reservation.reservation_id, {
      kind: next === "checked_in" ? "checked_in" : "checked_out",
      at: demoNowISO(),
    });
    showToast();
  }, [reservation, showToast]);

  const onAxis = useCallback((field, value, kind) => {
    if (!reservation) return;
    updateReservation(reservation.reservation_id, { [field]: value });
    logActivity(reservation.reservation_id, { kind, detail: value, at: demoNowISO() });
    showToast();
  }, [reservation, showToast]);

  const onAddPayment = useCallback((payload) => {
    if (!reservation) return;
    addPaymentEvent(reservation.reservation_id, { ...payload, at: demoNowISO() });
    logActivity(reservation.reservation_id, {
      kind: "payment",
      // Un importe sin su moneda no es una cantidad de dinero. El registro de
      // actividad lo escribe igual que el resto de la ficha, con la moneda del
      // dato y con el nombre del movimiento, no su clave interna.
      detail: `${PAYMENT_EVENT_KINDS[payload.kind]?.label || payload.kind}`
        + ` · ${formatMoney(payload.amount_eur, view.money.currency)}`,
      at: demoNowISO(),
    });
    showToast("Movimiento registrado en la vista previa");
  }, [reservation, view, showToast]);

  const onAddService = useCallback(({ service_id, quantity }) => {
    if (!reservation) return;
    addServiceOrder({
      service_id,
      quantity,
      reservation_id: reservation.reservation_id,
      villa_id: reservation.assigned_villa_id,
    });
    logActivity(reservation.reservation_id, { kind: "service", detail: service_id, at: demoNowISO() });
    showToast();
  }, [reservation, showToast]);

  const onRemoveService = useCallback((requestId) => {
    removeRequest(requestId);
    if (reservation) {
      logActivity(reservation.reservation_id, { kind: "service_removed", at: demoNowISO() });
    }
    showToast();
  }, [reservation, showToast]);

  const onCancel = useCallback((payload) => {
    if (!reservation) return;
    cancelReservation(reservation.reservation_id, { ...payload, cancelled_at: demoNowISO() });
    logActivity(reservation.reservation_id, {
      kind: "cancelled", detail: payload.reason, at: demoNowISO(),
    });
    showToast("Reserva cancelada en la vista previa");
  }, [reservation, showToast]);

  if (!view) {
    return (
      <PmsShell>
        <div className={`${SURFACE} p-6 text-center text-sm text-stone-500 dark:text-stone-400`}>
          <WarningCircle size={22} className="mx-auto mb-2 text-stone-400" />
          No existe ninguna reserva con el identificador{" "}
          <span className="font-mono">{id}</span> en la vista previa.
          <div className="mt-3">
            <Link to="/pms/reservations" className="underline text-stone-900 dark:text-stone-100">
              Volver al listado
            </Link>
          </div>
        </div>
      </PmsShell>
    );
  }

  const r = view.reservation;
  const cancelled = r.booking_status === "cancelled";

  return (
    <PmsShell>
      <button
        type="button"
        onClick={() => navigate("/pms/reservations")}
        data-testid="detail-back"
        className="mb-2 min-h-[44px] inline-flex items-center gap-1.5 text-xs text-stone-500 hover:text-stone-900 dark:hover:text-stone-100"
      >
        <ArrowLeft size={12} /> Reservas
      </button>

      <OverviewSection view={view} unitLabel={unitLabel} />

      <div className={`${SURFACE} p-3 mb-2 flex flex-wrap items-end gap-2`} data-testid="detail-actions">
        {!cancelled && r.stay_status === "pre_arrival" && (
          <QuickAction
            label="Registrar entrada"
            icon={DoorOpen}
            primary
            onClick={() => onStay("checked_in")}
            testId="action-check-in"
          />
        )}
        {!cancelled && r.stay_status === "checked_in" && (
          <QuickAction
            label="Registrar salida"
            icon={SignOut}
            primary
            onClick={() => onStay("checked_out")}
            testId="action-check-out"
          />
        )}

        <label className="min-w-0">
          <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">
            Estado de la reserva
          </span>
          <select
            value={r.booking_status}
            onChange={(e) => onAxis("booking_status", e.target.value, "status")}
            data-testid="action-booking-status"
            className="dv-input text-xs min-h-[44px]"
          >
            {Object.entries(BOOKING_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>

        <label className="min-w-0">
          <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">
            Estado de cobro
          </span>
          <select
            value={r.payment_status}
            onChange={(e) => onAxis("payment_status", e.target.value, "status")}
            data-testid="action-payment-status"
            className="dv-input text-xs min-h-[44px]"
          >
            {Object.entries(PAYMENT_STATUS_LABEL).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </label>

        {!cancelled && (
          <QuickAction
            label="Cancelar"
            icon={Prohibit}
            onClick={() => setCancelOpen(true)}
            testId="action-cancel"
          />
        )}

        <p className="text-[11px] text-stone-400 basis-full">
          Cada eje se cambia por separado: registrar la entrada no cobra la reserva, y confirmar no
          la marca como pagada.
        </p>
      </div>

      <GuestsSection
        view={view}
        onVerifyDocument={(id) => { verifyDocument(id); showToast("Documento comprobado"); }}
        onFileImmigration={() => {
          fileImmigration(r.reservation_id, { actor: "Dolce Admin", at: demoNowISO() });
          showToast("Reporte de inmigración marcado como presentado");
        }}
      />
      <StaySection
        view={view}
        settings={settings}
        unitLabel={unitLabel}
        onAssign={onAssign}
        onUnassign={onUnassign}
      />
      <PaymentsSection view={view} onAddPayment={onAddPayment} />
      <ServicesSection
        view={view}
        settings={settings}
        onAddService={onAddService}
        onRemoveService={onRemoveService}
      />
      <MessagesSection
        view={view}
        onMessage={() => showToast("Sin envío en la vista previa")}
      />
      <OperationsSection view={view} unitLabel={unitLabel} />
      <ActivitySection view={view} />

      {cancelOpen && (
        <CancelDialog
          view={view}
          onConfirm={onCancel}
          onClose={() => setCancelOpen(false)}
        />
      )}

      <PreviewToast text={toast} testId="reservation-toast" />
    </PmsShell>
  );
}
